import React, { useState, useEffect, useMemo } from 'react';
import { Outlet, Link, Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSupportContext } from '../context/SupportContext';
import { useTheme } from '../context/ThemeContext';
import { ACCESS_MODES, useEntitlements } from '../context/EntitlementContext';
import { navigateBackToPlatform } from '../utils/backToPlatformNavigation';
import {
  canCallAgencyStaffApis,
  isScopedSelfRepairSession,
} from '../utils/sessionScope';
import { isCanonicalSelfRepairScope } from '../utils/agencyRouteGuard';
import axios from 'axios';
import {
  LayoutDashboard,
  Users,
  FileText,
  Gavel,
  FileCheck,
  Upload,
  CheckSquare,
  Activity,
  Settings,
  ChevronDown,
  Shield,
  Menu,
  X,
  Command,
  Scale,
  ArrowLeft,
  Building2,
  CreditCard,
  Smartphone,
  Palette,
  Globe,
  MessageSquare,
  Database,
  Settings2,
  Link2,
  Lock,
  FlaskConical,
  User,
  KeyRound,
  DollarSign,
  Zap,
  Inbox,
  Mail,
  Sun,
  Moon,
  Monitor,
  Target,
  Wand2,
  Sparkles,
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';
import NotificationDropdown from '../components/notifications/NotificationDropdown';
import ProfileMenuDropdown from '../components/profile/ProfileMenuDropdown';
import { WORKSPACE_PRODUCTS } from '../config/productWorkspaceConfig';
import { getThemeTokens } from './SelfServePortalLayout';
import SupportModeBanner from '../components/platform/SupportModeBanner';
import CommandPalette from '../components/platform/CommandPalette';
import FundEliteBrandMark from '../components/branding/FundEliteBrandMark';
import { isAgencyFundGeniusChromePassthrough } from '../utils/fundeliteBrandingSurface';
import TaskReminderProvider from '../features/tasks/components/TaskReminderProvider';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

/**
 * Agency Workspace Layout with Sidebar Mode Switching
 * 
 * Two sidebar modes:
 * 1. WORKSPACE MODE - Normal agency navigation (Dashboard, Cases, etc.)
 * 2. SETTINGS MODE - Settings navigation (Agency Settings sections)
 * 
 * When user navigates to /agency/:agencyId/settings*, the sidebar switches to settings mode.
 * Clicking "Back to Agency" restores workspace mode.
 */

const NavItem = ({ icon: Icon, label, to, children, badge, openSection, onSectionToggle, isActive: forceActive }) => {
  const location = useLocation();
  const hasChildren = children && children.length > 0;
  const isOpen = openSection === label;
  
  const isActive = forceActive !== undefined ? forceActive : (to 
    ? location.pathname === to || location.pathname.startsWith(to + '/')
    : children?.some(c => location.pathname === c.to || location.pathname.startsWith(c.to + '/')));

  if (hasChildren) {
    return (
      <div className="mb-1">
        <button
          onClick={() => onSectionToggle(isOpen ? null : label)}
          className={cn(
            'w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-all',
            isActive 
              ? 'bg-white/15 text-white' 
              : 'text-white/70 hover:bg-white/10 hover:text-white'
          )}
          data-testid={`agency-nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
        >
          <div className="flex items-center gap-3">
            <Icon size={20} strokeWidth={1.5} />
            <span>{label}</span>
          </div>
          <div className="flex items-center gap-2">
            {badge && (
              <span className="px-2 py-0.5 text-xs bg-amber-500 text-white rounded-full">{badge}</span>
            )}
            <ChevronDown 
              size={16} 
              className={cn('transition-transform duration-200', isOpen ? 'rotate-0' : '-rotate-90')}
            />
          </div>
        </button>
        <div className={cn(
          'overflow-hidden transition-all duration-200 ease-out',
          isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        )}>
          <div className="mt-1 ml-4 pl-4 border-l border-white/10">
            {children.map((child, index) => (
              <Link
                key={index}
                to={child.to}
                className={cn(
                  'flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-all',
                  location.pathname === child.to || location.pathname.startsWith(child.to + '/')
                    ? 'bg-white/10 text-white'
                    : 'text-white/60 hover:bg-white/5 hover:text-white/90'
                )}
                data-testid={`agency-nav-sub-${child.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <span>{child.label}</span>
                {child.badge && (
                  <span className="px-2 py-0.5 text-xs bg-amber-500/80 text-white rounded-full">{child.badge}</span>
                )}
              </Link>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <Link
      to={to}
      className={cn(
        'flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium mb-1 transition-all',
        isActive
          ? 'bg-white/15 text-white'
          : 'text-white/70 hover:bg-white/10 hover:text-white'
      )}
      data-testid={`agency-nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-center gap-3">
        <Icon size={20} strokeWidth={1.5} />
        <span>{label}</span>
      </div>
      {badge && (
        <span className="px-2 py-0.5 text-xs bg-amber-500 text-white rounded-full">{badge}</span>
      )}
    </Link>
  );
};

/**
 * Settings Nav Item - simpler flat style for settings sidebar
 */
const SettingsNavItem = ({ icon: Icon, label, to, isActive, badge, onClick }) => {
  const Component = to ? Link : 'button';
  
  return (
    <Component
      to={to}
      onClick={onClick}
      className={cn(
        'w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium mb-1 transition-all',
        isActive
          ? 'bg-white/15 text-white'
          : 'text-white/70 hover:bg-white/10 hover:text-white'
      )}
      data-testid={`settings-nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-center gap-3">
        <Icon size={18} strokeWidth={1.5} />
        <span>{label}</span>
      </div>
      {badge && (
        <span className={cn(
          'px-2 py-0.5 text-[10px] font-semibold uppercase rounded-full',
          badge === 'locked' ? 'bg-slate-600 text-slate-300' 
            : badge === 'view' ? 'bg-blue-500/30 text-blue-300'
            : 'bg-emerald-500/30 text-emerald-300'
        )}>
          {badge}
        </span>
      )}
    </Component>
  );
};

const AgencyWorkspaceLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { agencyId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, token, logout, sessionReady } = useAuth();
  const { isInSupportMode, supportContext, exitSupportMode, isPlatformOwner } = useSupportContext();
  const { appearanceMode, setAppearanceMode, isDark } = useTheme();
  const agencyTokens = useMemo(
    () => getThemeTokens(isDark ? 'dark' : 'light'),
    [isDark],
  );
  
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openSection, setOpenSection] = useState(null);
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);
  const [agency, setAgency] = useState(null);
  const [settingsControl, setSettingsControl] = useState({});
  const [entitlements, setEntitlements] = useState({});
  const { getAccessMode, loading: entitlementsLoading } = useEntitlements();

  // Determine if we're in settings mode based on route
  const isSettingsMode = location.pathname.includes('/settings');
  const activeSettingsSection = searchParams.get('section') || 'profile';
  
  // Is platform admin?
  const isPlatformAdmin = user?.role === 'platform_owner' || user?.role === 'super_admin';
  const taskReminderWorkspace = useMemo(() => ({
    scopeType: 'agency',
    platformId: user?.platform_id || null,
    agencyId,
    timezone: agency?.timezone || user?.timezone || 'UTC',
    permissions: user?.permissions || [],
  }), [user, agency, agencyId]);

  // Fetch agency profile via Agency-scoped portal API (never owner-console).
  useEffect(() => {
    const fetchAgency = async () => {
      if (!sessionReady || !token || !agencyId) return;
      if (isScopedSelfRepairSession(user) || !canCallAgencyStaffApis(user)) {
        setAgency(null);
        return;
      }
      try {
        const response = await axios.get(`${API}/portal/staff/agency`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        });
        const payload = response.data || {};
        // Prefer session agency; if route agency differs, keep payload but stamp route id for UI.
        if (payload.agency_id && String(payload.agency_id) !== String(agencyId)) {
          console.warn(
            '[AgencyWorkspace] session agency_id differs from route',
            payload.agency_id,
            agencyId
          );
        }
        setAgency(payload);
      } catch (err) {
        console.error('Failed to fetch agency profile:', err);
        // Missing optional metadata must not break the Agency shell.
        setAgency(null);
      }
    };

    fetchAgency();
  }, [token, agencyId, user, sessionReady]);

  // Fetch settings control when in settings mode
  useEffect(() => {
    const fetchSettingsControl = async () => {
      try {
        const response = await axios.get(`${API}/admin/agencies/${agencyId}/settings-control`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSettingsControl(response.data?.settings_control || {});
      } catch (err) {
        setSettingsControl({});
      }
    };
    
    if (token && agencyId && isSettingsMode) {
      fetchSettingsControl();
    }
  }, [token, agencyId, isSettingsMode]);

  const handleReturnToPlatform = async () => {
    await navigateBackToPlatform({
      navigate,
      exitSupportMode: isInSupportMode ? exitSupportMode : undefined,
      hostname: typeof window !== 'undefined' ? window.location.hostname : '',
      from: typeof window !== 'undefined'
        ? `${window.location.pathname}${window.location.search || ''}`
        : '',
    });
  };

  const handleBackToAgency = () => {
    navigate(`/agency/${agencyId}/dashboard`);
  };

  // Auto-open section based on current route (workspace mode)
  useEffect(() => {
    if (isSettingsMode) return;
    
    const path = location.pathname;
    if (path.includes('/clients') || path.includes('/cases')) setOpenSection('Clients');
    else if (path.includes('/disputes') || path.includes('/letters')) setOpenSection('Dispute Queue');
    else if (path.includes('/credit-report')) setOpenSection('Credit Reports');
    else if (path.includes('/admin')) setOpenSection('Admin');
    else setOpenSection(null);
  }, [location.pathname, isSettingsMode]);

  // Command Palette shortcut
  useEffect(() => {
    if (!isPlatformOwner) return;
    
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsCommandPaletteOpen(prev => !prev);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isPlatformOwner]);

  // Settings control helpers
  const sectionToControlKey = {
    'profile': 'agency_profile',
    'billing': 'billing_membership',
    'staff': 'my_staff',
    'portal-access': 'portal_access',
    'portals-mobile': 'portals_mobile_app',
    'branding': 'branding_white_label',
    'domains': 'domains_redirects',
    'communication': 'communication_settings',
    'custom-fields': 'custom_fields',
    'custom-values': 'custom_values',
    'integrations': 'integrations',
    'private-integrations': 'private_integrations',
    'audit-logs': 'audit_logs',
    'labs': 'advanced_labs',
  };

  const getControlMode = (sectionKey) => {
    const controlKey = sectionToControlKey[sectionKey];
    return settingsControl[controlKey] || 'editable';
  };

  const isSectionVisible = (sectionKey) => {
    if (isPlatformAdmin) return true;
    return getControlMode(sectionKey) !== 'hidden';
  };

  const getSectionBadge = (sectionKey) => {
    if (isPlatformAdmin) return null;
    const mode = getControlMode(sectionKey);
    if (mode === 'locked') return 'locked';
    if (mode === 'read_only') return 'view';
    return null;
  };

  // Navigate to settings section
  const navigateToSection = (sectionKey) => {
    setSearchParams({ section: sectionKey });
  };

  // Workspace mode navigation items
  const workspaceNavItems = [
    { icon: LayoutDashboard, label: 'Dashboard', to: `/agency/${agencyId}/dashboard` },
    { icon: Inbox, label: 'Unified Inbox', to: `/agency/${agencyId}/inbox`, badge: 'New' },
    { icon: Target, label: 'Opportunities', to: `/agency/${agencyId}/opportunities` },
    { icon: Sparkles, label: 'FundGenius HQ', to: `/agency/${agencyId}/fundgenius-hq`, badge: 'New' },
    {
      icon: Users,
      label: 'Clients',
      children: [
        { label: 'Cases', to: `/agency/${agencyId}/clients/cases` },
        { label: 'Users', to: `/agency/${agencyId}/clients/users` },
        { label: 'My Assigned', to: `/agency/${agencyId}/clients/assigned` },
        { label: 'Unassigned', to: `/agency/${agencyId}/clients/unassigned` },
      ],
    },
    { icon: Wand2, label: 'Dispute Wizard', to: `/agency/${agencyId}/dispute-wizard` },
    {
      icon: Gavel,
      label: 'Dispute Queue',
      children: [
        { label: 'Active Disputes', to: `/agency/${agencyId}/disputes` },
        { label: 'Letters', to: `/agency/${agencyId}/letters` },
      ],
    },
    { icon: FileCheck, label: 'Document Requests', to: `/agency/${agencyId}/documents` },
    { icon: Upload, label: 'Upload Review', to: `/agency/${agencyId}/uploads` },
    { icon: CheckSquare, label: 'Tasks', to: `/agency/${agencyId}/tasks`, moduleKey: 'tasks' },
    { icon: Activity, label: 'Activity Feed', to: `/agency/${agencyId}/activity` },
    { icon: Scale, label: 'Letter Markup', to: `/agency/${agencyId}/collection-markup` },
    {
      icon: FileText,
      label: 'Credit Reports',
      children: [
        { label: 'Import Report', to: `/agency/${agencyId}/credit-report/import` },
        { label: 'Report Analysis', to: `/agency/${agencyId}/credit-report` },
      ],
    },
    { icon: DollarSign, label: 'Client Billing', to: `/agency/${agencyId}/client-billing` },
    { icon: Zap, label: 'Automations', to: `/agency/${agencyId}/automations` },
    { icon: Settings, label: 'Settings', to: `/agency/${agencyId}/settings` },
  ];
  const visibleWorkspaceNavItems = workspaceNavItems.filter((item) => {
    if (!item.moduleKey) return true;
    if (entitlementsLoading) return false;
    return getAccessMode(item.moduleKey) !== ACCESS_MODES.HIDDEN;
  });

  // Settings mode navigation structure
  const settingsSections = [
    {
      group: 'General',
      items: [
        { key: 'profile', label: 'Agency Profile', icon: Building2 },
        { key: 'billing', label: 'Billing & Membership', icon: CreditCard },
        { key: 'staff', label: 'My Staff', icon: Users },
      ],
    },
    {
      group: 'Portal & Branding',
      items: [
        { key: 'portal-access', label: 'Portal Access', icon: Shield },
        { key: 'portals-mobile', label: 'Portals & Mobile App', icon: Smartphone },
        { key: 'branding', label: 'Branding & White Label', icon: Palette },
        { key: 'domains', label: 'Domains & Redirects', icon: Globe },
      ],
    },
    {
      group: 'Communication & Services',
      items: [
        { key: 'communication', label: 'Communication Settings', icon: MessageSquare },
        { key: 'email-services', label: 'Email Services', icon: Mail, isRoute: true },
      ],
    },
    {
      group: 'Data & System',
      items: [
        { key: 'custom-fields', label: 'Custom Fields', icon: Database },
        { key: 'custom-values', label: 'Custom Values', icon: Settings2 },
        { key: 'integrations', label: 'Integrations', icon: Link2 },
        { key: 'private-integrations', label: 'Private Integrations', icon: Lock },
      ],
    },
    {
      group: 'Governance',
      items: [
        { key: 'audit-logs', label: 'Audit Logs', icon: FileText },
        { key: 'labs', label: 'Advanced / Labs', icon: FlaskConical, restricted: true },
      ],
    },
  ];

  const agencyName = agency?.name || supportContext?.agency_name || 'Agency';

  // Fail closed: Self Repair must never mount Agency chrome/sidebar.
  if (sessionReady && user && isCanonicalSelfRepairScope(user)) {
    return <Navigate to="/dashboard" replace data-testid="agency-shell-self-repair-redirect" />;
  }

  // FundGenius HQ owns its own shell. Do not wrap it in Agency FundElite chrome.
  if (isAgencyFundGeniusChromePassthrough(location.pathname)) {
    return (
      <div data-testid="agency-fundgenius-passthrough">
        <SupportModeBanner />
        <Outlet
          context={{
            agency,
            agencyId,
            settingsControl,
            entitlements,
            activeSettingsSection,
            navigateToSection,
            isPlatformAdmin,
            isSettingsMode,
          }}
        />
      </div>
    );
  }

  // Render Workspace Mode Sidebar
  const renderWorkspaceSidebar = () => (
    <>
      {/* Agency Header */}
      <div className="p-5 mb-2 border-b border-white/10">
        <div className="flex flex-col gap-2" data-testid="agency-sidebar-logo">
          <FundEliteBrandMark
            variant="agency-admin"
            mode="logo"
            logoClassName="h-8 w-auto max-w-[168px] object-contain object-left"
          />
          <div className="min-w-0">
            <h1 className="text-sm font-semibold text-white truncate">{agencyName}</h1>
            <p className="text-xs text-amber-400/80">Agency Workspace</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="px-2 pb-4 flex-1 overflow-y-auto">
        {visibleWorkspaceNavItems.map((item, index) => (
          <NavItem
            key={index}
            icon={item.icon}
            label={item.label}
            to={item.to}
            children={item.children}
            badge={item.badge}
            openSection={openSection}
            onSectionToggle={setOpenSection}
          />
        ))}
      </nav>

      {/* User Section with Theme Toggle */}
      <div className="mt-auto p-4 border-t border-white/10">
        {/* Theme Toggle */}
        <div className="flex items-center justify-between mb-3 pb-3 border-b border-white/10">
          <span className="text-xs text-white/50 uppercase tracking-wider">Appearance</span>
          <div className="flex items-center gap-1 p-1 bg-white/5 rounded-lg">
            <button
              onClick={() => setAppearanceMode('light')}
              className={cn(
                'p-1.5 rounded-md transition-all',
                appearanceMode === 'light' 
                  ? 'bg-emerald-500 text-white' 
                  : 'text-white/50 hover:text-white hover:bg-white/10'
              )}
              title="Light mode"
              data-testid="theme-light-btn"
            >
              <Sun size={14} />
            </button>
            <button
              onClick={() => setAppearanceMode('dark')}
              className={cn(
                'p-1.5 rounded-md transition-all',
                appearanceMode === 'dark' 
                  ? 'bg-emerald-500 text-white' 
                  : 'text-white/50 hover:text-white hover:bg-white/10'
              )}
              title="Dark mode"
              data-testid="theme-dark-btn"
            >
              <Moon size={14} />
            </button>
            <button
              onClick={() => setAppearanceMode('system')}
              className={cn(
                'p-1.5 rounded-md transition-all',
                appearanceMode === 'system' 
                  ? 'bg-emerald-500 text-white' 
                  : 'text-white/50 hover:text-white hover:bg-white/10'
              )}
              title="System preference"
              data-testid="theme-system-btn"
            >
              <Monitor size={14} />
            </button>
          </div>
        </div>
        
        {/* User Info */}
        <div className="flex items-center gap-3">
          <Avatar className="w-9 h-9">
            <AvatarImage src={user?.avatar} />
            <AvatarFallback className="bg-emerald-600 text-white text-sm">
              {user?.name?.charAt(0) || user?.email?.charAt(0) || 'U'}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">{user?.name || user?.email || 'User'}</p>
            <p className="text-xs text-white/50 truncate capitalize">{user?.role?.replace(/_/g, ' ') || 'Staff'}</p>
          </div>
        </div>
      </div>
    </>
  );

  // Render Settings Mode Sidebar
  const renderSettingsSidebar = () => (
    <>
      {/* Settings Header */}
      <div className="p-5 mb-2 border-b border-white/10">
        <div className="flex flex-col gap-2" data-testid="agency-settings-sidebar-logo">
          <FundEliteBrandMark
            variant="agency-admin"
            mode="logo"
            logoClassName="h-8 w-auto max-w-[168px] object-contain object-left"
          />
          <div className="min-w-0">
            <h1 className="text-sm font-semibold text-white truncate">{agencyName}</h1>
            <p className="text-xs text-amber-400/80">Settings</p>
          </div>
        </div>
      </div>

      {/* Back to Agency */}
      <div className="px-4 pb-4">
        <button
          onClick={handleBackToAgency}
          className="flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors"
          data-testid="back-to-agency-btn"
        >
          <ArrowLeft size={16} />
          <span>Back to Agency</span>
        </button>
      </div>

      {/* Agency Settings Sections */}
      <nav className="px-2 pb-4 flex-1 overflow-y-auto">
        {settingsSections.map((section) => {
            const visibleItems = section.items.filter(item => {
              if (item.restricted && !isPlatformAdmin) return false;
              if (!isSectionVisible(item.key)) return false;
              return true;
            });
            
            if (visibleItems.length === 0) return null;
            
            return (
              <div key={section.group} className="mb-4">
                <h4 className="px-3 mb-2 text-xs font-bold text-white/40 uppercase tracking-wider">
                  {section.group}
                </h4>
                {visibleItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = item.isRoute 
                    ? location.pathname.includes(`/settings/${item.key}`)
                    : activeSettingsSection === item.key;
                  const badge = getSectionBadge(item.key);
                  
                  // Handle route-based navigation (e.g., Email Services)
                  const handleClick = () => {
                    if (item.isRoute) {
                      navigate(`/agency/${agencyId}/settings/${item.key}`);
                    } else {
                      navigateToSection(item.key);
                    }
                  };
                  
                  return (
                    <button
                      key={item.key}
                      onClick={handleClick}
                      className={cn(
                        'w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-all mb-0.5',
                        isActive
                          ? 'bg-white/15 text-white'
                          : 'text-white/60 hover:bg-white/5 hover:text-white/90'
                      )}
                      data-testid={`settings-nav-${item.key}`}
                    >
                      <div className="flex items-center gap-3">
                        <Icon size={16} className={isActive ? 'text-emerald-400' : ''} />
                        <span>{item.label}</span>
                      </div>
                      {badge && (
                        <span className={cn(
                          'px-2 py-0.5 text-[10px] font-semibold uppercase rounded-full',
                          badge === 'locked' ? 'bg-slate-600 text-slate-300' 
                            : badge === 'view' ? 'bg-blue-500/30 text-blue-300'
                            : 'bg-emerald-500/30 text-emerald-300'
                        )}>
                          {badge}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </nav>

      {/* User Section with Theme Toggle */}
      <div className="mt-auto p-4 border-t border-white/10">
        {/* Theme Toggle */}
        <div className="flex items-center justify-between mb-3 pb-3 border-b border-white/10">
          <span className="text-xs text-white/50 uppercase tracking-wider">Appearance</span>
          <div className="flex items-center gap-1 p-1 bg-white/5 rounded-lg">
            <button
              onClick={() => setAppearanceMode('light')}
              className={cn(
                'p-1.5 rounded-md transition-all',
                appearanceMode === 'light' 
                  ? 'bg-emerald-500 text-white' 
                  : 'text-white/50 hover:text-white hover:bg-white/10'
              )}
              title="Light mode"
            >
              <Sun size={14} />
            </button>
            <button
              onClick={() => setAppearanceMode('dark')}
              className={cn(
                'p-1.5 rounded-md transition-all',
                appearanceMode === 'dark' 
                  ? 'bg-emerald-500 text-white' 
                  : 'text-white/50 hover:text-white hover:bg-white/10'
              )}
              title="Dark mode"
            >
              <Moon size={14} />
            </button>
            <button
              onClick={() => setAppearanceMode('system')}
              className={cn(
                'p-1.5 rounded-md transition-all',
                appearanceMode === 'system' 
                  ? 'bg-emerald-500 text-white' 
                  : 'text-white/50 hover:text-white hover:bg-white/10'
              )}
              title="System preference"
            >
              <Monitor size={14} />
            </button>
          </div>
        </div>
        
        {/* User Info */}
        <div className="flex items-center gap-3">
          <Avatar className="w-9 h-9">
            <AvatarImage src={user?.avatar} />
            <AvatarFallback className="bg-emerald-600 text-white text-sm">
              {user?.name?.charAt(0) || user?.email?.charAt(0) || 'U'}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">{user?.name || user?.email || 'User'}</p>
            <p className="text-xs text-white/50 truncate capitalize">{user?.role?.replace(/_/g, ' ') || 'Staff'}</p>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0B1F18]" data-testid="agency-workspace-layout">
      <TaskReminderProvider workspace={taskReminderWorkspace} />
      {/* Support Mode Banner */}
      <SupportModeBanner />

      {/* Command Palette */}
      {isPlatformOwner && (
        <CommandPalette 
          isOpen={isCommandPaletteOpen} 
          onClose={() => setIsCommandPaletteOpen(false)} 
        />
      )}
      
      {/* Sidebar - switches between workspace and settings mode */}
      <aside 
        className={cn(
          'fixed inset-y-0 left-0 z-40 w-64 bg-gradient-to-b from-stone-900 to-stone-800 transform transition-transform duration-300 ease-in-out flex flex-col',
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        )}
        data-testid="agency-sidebar"
      >
        {isSettingsMode ? renderSettingsSidebar() : renderWorkspaceSidebar()}
      </aside>

      {/* Main Content */}
      <div className="lg:ml-64 min-h-screen flex flex-col">
        {/* Top Bar */}
        <header className="bg-white border-b border-slate-200 px-4 lg:px-6 py-3 flex items-center justify-between sticky top-0 z-30">
          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-slate-100"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          {/* Page Title */}
          <div className="flex items-center gap-4">
            <div>
              <h2 className="text-lg font-semibold text-stone-900">{agencyName}</h2>
              <p className="text-sm text-stone-500">
                {isSettingsMode ? 'Agency Settings' : 'Agency Workspace'}
              </p>
            </div>
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-3">
            {/* Command Palette Trigger */}
            {isPlatformOwner && (
              <button
                onClick={() => setIsCommandPaletteOpen(true)}
                className="hidden lg:flex items-center gap-2 px-3 py-1.5 text-sm text-stone-500 bg-stone-100 rounded-lg hover:bg-stone-200 transition-colors"
              >
                <Command size={14} />
                <span>Quick Search</span>
                <kbd className="ml-2 px-1.5 py-0.5 text-xs bg-stone-200 rounded">⌘K</kbd>
              </button>
            )}

            {/* Notifications + profile (agency-specific adapters) */}
            <NotificationDropdown
              tokens={agencyTokens}
              product={WORKSPACE_PRODUCTS.AGENCY}
            />
            <ProfileMenuDropdown
              user={user}
              tokens={agencyTokens}
              product={WORKSPACE_PRODUCTS.AGENCY}
              managePlanPath={agencyId ? `/agency/${agencyId}/settings?section=billing` : undefined}
              onLogout={logout}
              resolvedTheme={isDark ? 'dark' : 'light'}
              theme={appearanceMode}
              onThemeChange={setAppearanceMode}
            />

            {/* Return to Platform Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleReturnToPlatform}
              className="hidden lg:flex items-center gap-2"
            >
              <ArrowLeft size={16} />
              Return to Platform
            </Button>
          </div>
        </header>

        {/* Page Content — Tasks sit flush on the agency page background (no padded light wrapper). */}
        <main
          className={cn(
            'flex-1',
            location.pathname.includes('/tasks')
              ? 'bg-transparent'
              : 'p-4 lg:p-6',
          )}
          data-testid={location.pathname.includes('/tasks') ? 'agency-tasks-shell' : undefined}
        >
          <Outlet context={{ 
            agency, 
            agencyId, 
            settingsControl,
            entitlements,
            activeSettingsSection,
            navigateToSection,
            isPlatformAdmin,
            isSettingsMode,
          }} />
        </main>
      </div>

      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-[rgba(6,28,20,0.75)] z-30 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
    </div>
  );
};

export default AgencyWorkspaceLayout;
